<template>
  <div class="auth">
    <div class="login-form">
      <div class="form-left">
        <div class="language">
          <ui-select-language
            menuContentClass="language-select-menu"
            :applyImmediately="true"
          />
        </div>
        <div class="mb-10 mt-30">
          <img alt="BeMob" src="/images/logo-white2.svg" />
        </div>
        <p class="mb-32 pr-30">
          {{
            t(
              'BeMob helps to manage your ad campaigns, identify the most profitable traffic sources, measure your landing pages performance and provide highly detailed real-time reports.',
            )
          }}
        </p>
        <div class="login-signup">
          <div>{{ t("Don't have an account?") }}</div>
          <ui-button
            class="mt-4"
            :to="{ path: 'register' }"
            :text="t('Sign up')"
            icon="fas fa-user-plus"
          />
        </div>
        <div class="copyright">
          2015-{{ moment().year() }}
          {{ t('© BeMob. All rights reserved.') }}
        </div>
      </div>

      <div class="form-right">
        <div class="form-right-inner">
          <div class="login-title">{{ t('Login to BeMob') }}</div>
          <div
            class="error-block mb-22 d-flex align-center justify-start"
            v-if="apiErrors"
          >
            <div class="error-text">
              <ui-icon size="12" class="mr-8" icon="fas fa-exclamation-triangle" />
              {{ apiErrors }}
            </div>
          </div>
          <ui-input
            class="ui-input__login"
            autofocus
            @keyup.enter="submit"
            append-inner-icon="fas fa-envelope"
            :label="t('Email')"
            autocomplete="email"
            :placeholder="t('Email')"
            type="email"
            v-model="form.email"
            name="email"
          />

          <ui-input
            class="ui-input__login"
            :label="t('Password')"
            :placeholder="t('Password')"
            v-model="form.password"
            autocomplete="current-password"
            name="password"
            type="password"
            append-inner-icon="fas fa-key"
            @keyup.enter="submit"
          />
          <div class="w100">
            <ui-checkbox
              v-model="form.rememberMe"
              class="mb-8"
              :label="t('Remember me')"
              :color="$colors.blue"
            />
          </div>
          <div v-if="showRecaptcha" class="login-recaptcha">
            <RecaptchaV2
              :key="recaptchaResetKey"
              @expired-callback="onRecaptchaExpired"
              @load-callback="onRecaptchaVerify"
            />
          </div>
          <ui-button
            button-style="contained"
            :loading="apiLoading"
            :disabled="showRecaptcha && !form.recaptchaResponse"
            @click="submit"
            iconAppend="fas fa-arrow-right"
            class="w100"
            :text="t('Sign in')"
          />
          <div class="link-forgot mt-8">
            <span class="link" @click="handleForgotPassword">
              {{ t('Forgot password?') }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { RecaptchaV2 } from 'vue3-recaptcha-v2';

import { ref, reactive } from 'vue';
import { useRouter } from 'vue-router';
import moment from 'moment-timezone';
import { useColors, useI18n } from '@/composables';
import { loginApiRequest } from '@/services/api/auth/auth-login-api';
import type { LoginForm } from '@/services/api/auth/auth-login-api';
import { setAuthToken } from '@/services/api/main/axios';
import { loadProfileData } from '@/services/api/api-services/load-profile';
import { getDashboardLink } from '@/utils/get-dashboard-link';
import { useStore } from '@/store';

const router = useRouter();
const { t } = useI18n();
const $colors = useColors();
const showRecaptcha = ref(false);
const recaptchaResetKey = ref(0);

const form = reactive({
  email: '',
  password: '',
  rememberMe: true,
  recaptchaResponse: '',
});

const apiLoading = ref(false);
const apiErrors = ref('');

const onShowRecaptcha = () => {
  showRecaptcha.value = true;
};

const onRecaptchaReset = () => {
  form.recaptchaResponse = '';
  recaptchaResetKey.value++;
};

const onClearPassword = () => {
  form.password = '';
};

const apiSubmit = async (loginForm: LoginForm) => {
  apiErrors.value = '';
  apiLoading.value = true;

  try {
    const res = await loginApiRequest(loginForm);
    const payload = res.data.payload;

    setAuthToken(payload.id);

    if (payload.ttl === 0) {
      return router.push('/two-factor');
    }

    await loadProfileData();
    const store = useStore();
    router.push(getDashboardLink(store.user));
  } catch (err: unknown) {
    const status = (err as { response?: { status?: number } })?.response?.status;
    onRecaptchaReset();

    if (status === 401 || status === 422) {
      onShowRecaptcha();
    }

    if (status === 400 || status === 401 || status === 403 || status === 429) {
      onClearPassword();
    }

    if (status === 429) {
      apiErrors.value = t(
        "You've reached the maximum login attempts. Try again in 60 minutes.",
      );
    }

    if (status === 400 || status === 401 || status === 403) {
      apiErrors.value = t('Email / Password is invalid');
    }

    throw err;
  } finally {
    apiLoading.value = false;
  }
};

const onRecaptchaVerify = (response: string) => {
  form.recaptchaResponse = response;
};

const onRecaptchaExpired = () => {
  form.recaptchaResponse = '';
};

const handleForgotPassword = () => {
  router.push({ name: 'forgot-password' });
};

const submit = () => {
  apiSubmit(form);
};
</script>
